import { useCallback, useEffect, useState, useMemo } from 'react';
import {
  transformAllUserWorkspaces,
  TransformedUserWorkspace,
} from '@/utils/data/transformWorkSpaceItems';
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';
import { uploadLocalDocuments } from '@/apis/data/document/post/uploadLocalDocuments';
import { uploadUrlDocuments } from '@/apis/data/document/post/uploadUrlDocuments';
import { deleteDocument } from '@/apis/data/document/delete/deleteDocument';
import { downloadDocumentBytes } from '@/apis/data/document/get/downloadDocumentBytes';
import { downloadDocumentStream } from '@/apis/data/document/get/downloadDocumentStream';
import { postCollection } from '@/apis/data/dataRoom/post/postCollection';
import { linkFileToAsk } from '@/apis/data/dataRoom/post/linkFileToAsk';
import { assignDocumentToCollection } from '@/apis/data/document/post/assignDocumentToCollection';
import { deleteCollectionFile } from '@/apis/data/dataRoom/delete/deleteCollectionFile';
import {
  getCollectionSuggestions,
  CollectionSuggestion,
} from '@/apis/data/document/post/getCollectionSuggestions';
import { filterDocumentsByCollection } from '@/utils/documents/documentFilters';
import { getFileCollectionIdHelper } from '@/utils/documents/getFileCollectionId';
import { getFileCollectionInfoHelper } from '@/utils/documents/getFileCollectionInfo';
import { DataRoomFile, Collection } from '@/types/documents/documents.types';
import { AuthContextProps } from '@/contexts/AuthContext';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import {
  ApiError,
  getErrorResponseAttachment,
} from '@/utils/errors/apiErrorWrapper';
import { AskSession } from '@/apis/ask/askHistory/types';

const resolveAskSessionFileId = (file: string | DataRoomFile): string => {
  if (typeof file === 'string') {
    return file;
  }
  return file.dataRoomFileId || file.id || '';
};

const isCollection = (item: unknown): item is Collection =>
  typeof item === 'object' &&
  item !== null &&
  ('collectionId' in item || 'id' in item || 'name' in item || 'title' in item);

interface UseDocumentsParams {
  workspaceId: string | undefined;
  getAccessToken: AuthContextProps['getAccessToken'];
}

interface GetWorkspaceDataOptions {
  silent?: boolean;
}

export const useDocuments = ({
  workspaceId,
  getAccessToken,
}: UseDocumentsParams) => {
  // Error handling hook - provides consistent error logging and toast notifications
  const { handleError, handleSuccess, handleWarning, handleInfo } =
    useWorkspaceErrorHandler(workspaceId);

  const [workspaceData, setWorkspaceData] =
    useState<TransformedUserWorkspace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentOperation, setCurrentOperation] = useState<
    'upload' | 'delete' | null
  >(null);
  // Real upload progress (0-100) of the request body, or null when not uploading.
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // Surfaced upload failure so the UI can offer a retry affordance.
  const [uploadError, setUploadError] = useState<{
    message: string;
    mode: 'files' | 'url';
  } | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadMode, setUploadMode] = useState<'files' | 'url'>('files');
  const [showCreateCollectionModal, setShowCreateCollectionModal] =
    useState(false);
  const [showCreateSmartCollectionModal, setShowCreateSmartCollectionModal] =
    useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [urlInput, setUrlInput] = useState('');
  const [versionIfExists, setVersionIfExists] = useState(true);
  const [allowSmartCategorization, setAllowSmartCategorization] =
    useState(true);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
  const [selectedAskSessionId, setSelectedAskSessionId] = useState<string>('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [activeCollectionFilter, setActiveCollectionFilter] = useState<
    string | null
  >(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionDescription, setNewCollectionDescription] = useState('');
  const [newSmartCollectionName, setNewSmartCollectionName] = useState('');
  const [newSmartCollectionDescription, setNewSmartCollectionDescription] =
    useState('');
  const [showMoveToCollectionModal, setShowMoveToCollectionModal] =
    useState(false);
  const [selectedDocumentForMove, setSelectedDocumentForMove] =
    useState<DataRoomFile | null>(null);
  const [targetCollectionId, setTargetCollectionId] = useState<string>('');
  const [collectionSuggestions, setCollectionSuggestions] = useState<
    Record<string, CollectionSuggestion[]>
  >({});
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showDocumentSuggestionsModal, setShowDocumentSuggestionsModal] =
    useState(false);
  const [selectedDocumentForSuggestions, setSelectedDocumentForSuggestions] =
    useState<DataRoomFile | null>(null);
  const [documentSuggestions, setDocumentSuggestions] = useState<
    CollectionSuggestion[]
  >([]);
  const [isLoadingDocumentSuggestions, setIsLoadingDocumentSuggestions] =
    useState(false);
  const [showPdfViewerModal, setShowPdfViewerModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DataRoomFile | null>(
    null,
  );
  const [pdfStreamUrl, setPdfStreamUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [viewMode, setViewMode] = useState<'file' | 'folder'>('file');
  const [showLinkFileToAskModal, setShowLinkFileToAskModal] = useState(false);
  const [selectedDocumentForLink, setSelectedDocumentForLink] =
    useState<DataRoomFile | null>(null);
  const [selectedAskSessionIdForLink, setSelectedAskSessionIdForLink] =
    useState<string>('');

  const getWorkspaceData = useCallback(
    async (options?: GetWorkspaceDataOptions) => {
      try {
        if (!options?.silent) {
          setIsLoading(true);
        }
        setError(null);
        const data = await getWorkspace({
          getAccessToken: getAccessToken,
          workspaceId: workspaceId || '',
        });
        const transformed = await transformAllUserWorkspaces(
          [data],
          getAccessToken,
        );
        setWorkspaceData(transformed[0]);
      } catch (err) {
        // Critical error: Log, set state, and show toast
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to load workspace data');

        // Extract response data if available (for Azure error parsing)
        const response = getErrorResponseAttachment(err);

        // Log for debugging
        console.error('Failed to load workspace data:', error);

        // Set error state for components that want to react
        setError(error);

        // Log to error boundary and show toast
        handleError(
          error instanceof ApiError ? error : error,
          'Failed to load workspace data',
          response,
        );
      } finally {
        if (!options?.silent) {
          setIsLoading(false);
        }
      }
    },
    [workspaceId, getAccessToken, handleError],
  );

  useEffect(() => {
    getWorkspaceData();
  }, [getWorkspaceData]);

  const documents = useMemo((): DataRoomFile[] => {
    return (
      workspaceData?.dataRoom?.workspaceDataRoomFiles?.items?.filter(
        (item): item is DataRoomFile => {
          const file = item as DataRoomFile;
          return (
            file &&
            typeof file === 'object' &&
            'dataRoomFileId' in file &&
            !file.markedForDeletion
          );
        },
      ) ?? []
    );
  }, [workspaceData?.dataRoom?.workspaceDataRoomFiles?.items]);

  // Get workflows and collections from workspaceData
  const workflows = Array.isArray(workspaceData?.workflows)
    ? workspaceData.workflows
    : [];
  const allCollections: Collection[] = (
    workspaceData?.dataRoom?.workspaceDataRoomCollections?.items ?? []
  ).filter(isCollection);

  // Helper function to find which collection a file belongs to
  // Checks collections' files objects to determine if a file is in a collection
  const getFileCollectionId = useCallback(
    (fileId: string) => getFileCollectionIdHelper(fileId, allCollections),
    [allCollections],
  );

  const getFileCollectionInfo = useCallback(
    (fileId: string) => getFileCollectionInfoHelper(fileId, allCollections),
    [allCollections],
  );

  // Build a map of fileId -> askSession[] for cross-referencing
  const fileToAskSessionsMap = useMemo(() => {
    const map: Record<string, AskSession[]> = {};

    if (workspaceData?.askSessions) {
      workspaceData.askSessions.forEach((askSession: AskSession) => {
        // Handle both string[] (file IDs) and DataRoomFile[] formats
        const fileIds: string[] = Array.isArray(askSession.files)
          ? (askSession.files as Array<string | DataRoomFile>).map(
              resolveAskSessionFileId,
            )
          : [];

        fileIds.forEach((fileId) => {
          if (fileId) {
            if (!map[fileId]) {
              map[fileId] = [];
            }
            map[fileId].push(askSession);
          }
        });
      });
    }

    return map;
  }, [workspaceData?.askSessions]);

  // Enrich documents with their collection ID and ask session data
  const documentsWithCollection = useMemo(
    () =>
      documents.map((doc) => ({
        ...doc,
        collectionId:
          getFileCollectionId(doc.dataRoomFileId) ||
          doc.collectionId ||
          doc.dataRoomCollectionId,
        collectionInfo: getFileCollectionInfo(doc.dataRoomFileId),
        askSessions: fileToAskSessionsMap[doc.dataRoomFileId] || [],
      })),
    [
      documents,
      getFileCollectionId,
      getFileCollectionInfo,
      fileToAskSessionsMap,
    ],
  );

  // Separate regular and smart collections
  const regularCollections = allCollections.filter(
    (col: Collection) => !col.isSmartCollection,
  );
  const smartCollections = allCollections.filter(
    (col: Collection) => col.isSmartCollection,
  );

  // Filter documents by collection if a collection is selected
  const filteredDocuments = filterDocumentsByCollection(
    documentsWithCollection,
    activeCollectionFilter,
  );

  const confirmFileUpload = async () => {
    if (!workspaceId || pendingFiles.length === 0) return;

    try {
      setIsUploading(true);
      setCurrentOperation('upload');
      setUploadError(null);
      setUploadProgress(0);
      setShowUploadModal(false);

      // Show uploading toast
      handleInfo(
        `Uploading ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}...`,
      );

      // Smart categorization: If enabled and no collection selected,
      // don't send collectionId to allow server-side auto-categorization
      // If a collection is explicitly selected, use it (smart categorization may be disabled)
      const collectionIdToUse =
        allowSmartCategorization && !selectedCollectionId
          ? undefined
          : selectedCollectionId || undefined;

      const uploadResponse = await uploadLocalDocuments({
        getAccessToken,
        workspaceId: workspaceId,
        files: pendingFiles,
        versionIfExists,
        workflowInstanceId: selectedWorkflowId || undefined,
        askSessionId: selectedAskSessionId || undefined,
        collectionId: collectionIdToUse,
        onProgress: setUploadProgress,
      });

      // Reset form
      setPendingFiles([]);
      setSelectedWorkflowId('');
      setSelectedAskSessionId('');
      setSelectedCollectionId('');
      setCollectionSuggestions({});

      // On successful 200 response, refresh workspace data to populate added files
      if (uploadResponse && (uploadResponse.success || uploadResponse.data)) {
        // Upload succeeded - now files are being processed
        setIsUploading(false);
        setUploadProgress(null);
        setIsProcessing(true);

        await getWorkspaceData();
        // Show success message
        handleSuccess(
          `Successfully uploaded ${pendingFiles.length} file${
            pendingFiles.length > 1 ? 's' : ''
          }`,
        );
      } else {
        setIsUploading(false);
        setUploadProgress(null);
      }
    } catch (err) {
      // Action error: Log, show toast, optionally set state
      const error =
        err instanceof Error ? err : new Error('Failed to upload documents');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to upload documents:', error);

      // Set error state (optional - components can react if needed)
      setError(error);
      setUploadError({
        message:
          error instanceof ApiError ? error.customerMessage : error.message,
        mode: 'files',
      });

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to upload documents',
        response,
      );
      setIsUploading(false);
      setIsProcessing(false);
      setCurrentOperation(null);
      setUploadProgress(null);
    }
  };

  const handleUrlUpload = async () => {
    if (!workspaceId || !urlInput.trim()) return;

    const urls = urlInput
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);

    if (urls.length === 0) {
      // Validation error: Show warning toast (not an error)
      handleWarning('Please enter at least one valid URL');
      return;
    }

    try {
      setIsUploading(true);
      setCurrentOperation('upload');
      setUploadError(null);

      // Show uploading toast
      handleInfo(
        `Uploading ${urls.length} URL${urls.length > 1 ? 's' : ''}...`,
      );

      // Smart categorization: If enabled and no collection selected,
      // don't send collectionId to allow server-side auto-categorization
      const collectionIdToUse =
        allowSmartCategorization && !selectedCollectionId
          ? undefined
          : selectedCollectionId || undefined;

      const uploadResponse = await uploadUrlDocuments({
        getAccessToken,
        workspaceId: workspaceId,
        urlCollection: urls,
        versionIfExists,
        workflowId: selectedWorkflowId || undefined,
        askSessionId: selectedAskSessionId || undefined,
        collectionId: collectionIdToUse,
      });
      setShowUploadModal(false);
      setUrlInput('');
      setSelectedWorkflowId('');
      setSelectedAskSessionId('');
      setSelectedCollectionId('');

      if (uploadResponse && (uploadResponse.success || uploadResponse.data)) {
        // Upload succeeded - now files are being processed
        setIsUploading(false);
        setIsProcessing(true);

        await getWorkspaceData();
        // Show success message
        handleSuccess(
          `Successfully uploaded ${urls.length} URL${urls.length > 1 ? 's' : ''}`,
        );
      } else {
        setIsUploading(false);
      }
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error ? err : new Error('Failed to upload from URL');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to upload from URL:', error);

      // Set error state (optional)
      setError(error);
      setUploadError({
        message:
          error instanceof ApiError ? error.customerMessage : error.message,
        mode: 'url',
      });

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to upload from URL',
        response,
      );
      setIsUploading(false);
      setIsProcessing(false);
      setCurrentOperation(null);
    }
  };

  const handleDelete = async (dataRoomFileIds: string | string[]) => {
    if (!workspaceId) return 0;

    const documentIds = Array.isArray(dataRoomFileIds)
      ? dataRoomFileIds
      : [dataRoomFileIds];

    if (documentIds.length === 0) return 0;

    try {
      setIsUploading(true);
      setCurrentOperation('delete');
      let submittedCount = 0;

      // Delete documents sequentially so we don't overload the API and
      // can surface per-document errors while continuing with the rest.
      for (const dataRoomFileId of documentIds) {
        try {
          await deleteDocument({
            getAccessToken,
            workspaceId: workspaceId,
            documentId: dataRoomFileId,
          });
          submittedCount += 1;
        } catch (err) {
          const error =
            err instanceof Error ? err : new Error('Failed to delete document');
          const response = getErrorResponseAttachment(err);

          console.error('Failed to delete document:', error);

          handleError(
            error instanceof ApiError ? error : error,
            'Failed to delete document',
            response,
          );
        }
      }

      // Deletion requests have been sent; wait for SignalR to confirm and refresh.
      setIsUploading(false);
      setIsProcessing(true);

      if (submittedCount > 0) {
        handleSuccess(
          submittedCount === 1
            ? 'Delete request submitted for 1 document'
            : `Delete request submitted for ${submittedCount} documents`,
        );
      } else {
        handleWarning('No documents were queued for deletion');
      }
      return submittedCount;
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to delete documents');
      const response = getErrorResponseAttachment(err);

      console.error('Failed to delete documents:', error);

      handleError(
        error instanceof ApiError ? error : error,
        'Failed to delete documents',
        response,
      );
      setIsUploading(false);
      setIsProcessing(false);
      setCurrentOperation(null);
      return 0;
    }
  };

  const handleDownload = async (doc: DataRoomFile) => {
    if (!workspaceId) return;

    try {
      const blob = await downloadDocumentBytes({
        getAccessToken,
        workspaceId: workspaceId,
        documentId: doc.dataRoomFileId,
        type: 'CONVERSION',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      // Show success message
      handleSuccess(`Downloaded ${doc.name || 'document'}`);
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error ? err : new Error('Failed to download document');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to download document:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to download document',
        response,
      );
    }
  };

  const handleViewDocument = async (doc: DataRoomFile) => {
    if (!workspaceId) return;

    setSelectedDocument(doc);
    setShowPdfViewerModal(true);
    setIsLoadingPdf(true);
    setPdfStreamUrl(null);

    try {
      const streamUrl = await downloadDocumentStream({
        getAccessToken,
        workspaceId: workspaceId,
        documentId: doc.dataRoomFileId,
        type: 'CONVERSION',
      });
      setPdfStreamUrl(streamUrl);
    } catch (err) {
      // Action error: Log, show toast, close modal
      const error =
        err instanceof Error ? err : new Error('Failed to load document');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to load document:', error);

      // Close modal on error
      setShowPdfViewerModal(false);
      setSelectedDocument(null);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to load document',
        response,
      );
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleFileSelect = (files: File[]) => {
    if (files.length > 0) {
      setPendingFiles(files);
      setUploadMode('files');

      // Fetch AI suggestions for each file if smart categorization is enabled
      if (allowSmartCategorization) {
        setIsLoadingSuggestions(true);
        const suggestionsMap: Record<string, CollectionSuggestion[]> = {};

        (async () => {
          try {
            // Prepare existing collections for matching
            const existingCollections = allCollections.map((col) => ({
              id: col.id || col.collectionId || '',
              name: col.name || col.title || '',
              description: col.description || '',
            }));

            // Get suggestions for each file
            await Promise.all(
              files.map(async (file) => {
                try {
                  const suggestions = await getCollectionSuggestions({
                    getAccessToken,
                    workspaceId: workspaceId || '',
                    file,
                    existingCollections,
                  });
                  suggestionsMap[file.name] = suggestions;
                } catch (err) {
                  // Non-critical error: Log only (silent failure)
                  // Suggestions are nice-to-have, so we don't show toast or break the flow
                  console.error(
                    `Failed to get suggestions for ${file.name}:`,
                    err,
                  );
                  suggestionsMap[file.name] = [];
                }
              }),
            );

            setCollectionSuggestions(suggestionsMap);
          } catch (err) {
            // Non-critical error: Log only (silent failure)
            console.error('Failed to get collection suggestions:', err);
          } finally {
            setIsLoadingSuggestions(false);
          }
        })();
      } else {
        setCollectionSuggestions({});
      }
    }
  };

  const handleGetDocumentSuggestions = async (doc: DataRoomFile) => {
    if (!workspaceId) return;

    setSelectedDocumentForSuggestions(doc);
    setShowDocumentSuggestionsModal(true);
    setIsLoadingDocumentSuggestions(true);
    setDocumentSuggestions([]);

    try {
      const existingCollections = allCollections.map((col) => ({
        id: col.id || col.collectionId || '',
        name: col.name || col.title || '',
        description: col.description || '',
      }));

      // Create a mock File object from the document for the suggestion function
      // We'll use the document name and synopsis to generate suggestions
      const mockFile = new File([], doc.name || 'document', {
        type: doc.mimeType || 'application/octet-stream',
      });

      const suggestions = await getCollectionSuggestions({
        getAccessToken,
        workspaceId: workspaceId,
        file: mockFile,
        existingCollections,
      });

      setDocumentSuggestions(suggestions);
    } catch (err) {
      // Non-critical error: Log only (silent failure)
      // Suggestions are nice-to-have, so we don't show toast
      console.error('Failed to get document suggestions:', err);
      setDocumentSuggestions([]);
    } finally {
      setIsLoadingDocumentSuggestions(false);
    }
  };

  const handleMoveToCollection = async () => {
    if (!workspaceId || !selectedDocumentForMove) return;

    try {
      setIsUploading(true);

      const currentCollectionId =
        getFileCollectionId(selectedDocumentForMove.dataRoomFileId) ||
        selectedDocumentForMove.collectionId ||
        selectedDocumentForMove.dataRoomCollectionId;

      if (!targetCollectionId) {
        if (currentCollectionId) {
          await deleteCollectionFile({
            getAccessToken,
            workspaceId: workspaceId,
            collectionId: currentCollectionId,
            fileId: selectedDocumentForMove.dataRoomFileId,
          });
        }
      } else {
        if (currentCollectionId && currentCollectionId !== targetCollectionId) {
          try {
            await deleteCollectionFile({
              getAccessToken,
              workspaceId: workspaceId,
              collectionId: currentCollectionId,
              fileId: selectedDocumentForMove.dataRoomFileId,
            });
          } catch (err) {
            // Non-critical error: Log warning only (silent failure)
            // Removing from old collection is best-effort
            console.warn('Failed to remove from old collection:', err);
          }
        }

        await assignDocumentToCollection({
          getAccessToken,
          workspaceId: workspaceId,
          collectionId: targetCollectionId,
          documentId: selectedDocumentForMove.dataRoomFileId,
        });
      }

      setShowMoveToCollectionModal(false);
      setSelectedDocumentForMove(null);
      setTargetCollectionId('');

      await getWorkspaceData();
      // Show success message
      handleSuccess('Document moved to collection successfully');
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to move document to collection');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to move document to collection:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to move document to collection',
        response,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateCollection = async () => {
    if (!workspaceId || !newCollectionName.trim()) {
      // Validation error: Show warning toast (not an error)
      handleWarning('Please enter a collection name');
      return;
    }

    try {
      setIsUploading(true);

      await postCollection({
        getAccessToken,
        workspaceId: workspaceId,
        body: {
          name: newCollectionName,
          description: newCollectionDescription || '',
          isSmartCollection: false,
        },
      });

      setShowCreateCollectionModal(false);
      setNewCollectionName('');
      setNewCollectionDescription('');

      await getWorkspaceData();
      // Show success message
      handleSuccess(`Collection "${newCollectionName}" created successfully`);
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error ? err : new Error('Failed to create collection');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to create collection:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to create collection',
        response,
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateSmartCollection = async () => {
    if (!workspaceId || !newSmartCollectionName.trim()) {
      // Validation error: Show warning toast (not an error)
      handleWarning('Please enter a smart collection name');
      return;
    }

    try {
      setIsUploading(true);

      await postCollection({
        getAccessToken,
        workspaceId: workspaceId,
        body: {
          name: newSmartCollectionName,
          description: newSmartCollectionDescription || '',
          isSmartCollection: true,
        },
      });

      setShowCreateSmartCollectionModal(false);
      setNewSmartCollectionName('');
      setNewSmartCollectionDescription('');

      await getWorkspaceData();
      // Show success message
      handleSuccess(
        `Smart collection "${newSmartCollectionName}" created successfully`,
      );
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to create smart collection');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to create smart collection:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to create smart collection',
        response,
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Function to clear processing state when SignalR confirms processing is complete
  const clearProcessingState = useCallback(() => {
    setIsProcessing(false);
    setCurrentOperation(null);
  }, []);

  /**
   * Handles linking a file to an ask session.
   */
  const handleLinkFileToAsk = async () => {
    if (
      !workspaceId ||
      !selectedDocumentForLink ||
      !selectedAskSessionIdForLink
    ) {
      handleWarning('Please select a document and an ask session');
      return;
    }

    try {
      setIsUploading(true);

      await linkFileToAsk({
        getAccessToken,
        workspaceId: workspaceId,
        body: {
          dataRoomFileId: selectedDocumentForLink.dataRoomFileId,
          instanceId: selectedAskSessionIdForLink,
        },
      });

      setShowLinkFileToAskModal(false);
      setSelectedDocumentForLink(null);
      setSelectedAskSessionIdForLink('');

      await getWorkspaceData();
      handleSuccess('File linked to ask session successfully');
    } catch (err) {
      // Action error: Log, show toast
      const error =
        err instanceof Error
          ? err
          : new Error('Failed to link file to ask session');

      // Extract response data if available
      const response = getErrorResponseAttachment(err);

      // Log for debugging
      console.error('Failed to link file to ask session:', error);

      // Log to error boundary and show toast
      handleError(
        error instanceof ApiError ? error : error,
        'Failed to link file to ask session',
        response,
      );
    } finally {
      setIsUploading(false);
    }
  };

  return {
    // State
    workspaceData,
    isLoading,
    isUploading,
    isProcessing,
    currentOperation,
    uploadProgress,
    uploadError,
    error,
    showUploadModal,
    uploadMode,
    showCreateCollectionModal,
    showCreateSmartCollectionModal,
    pendingFiles,
    urlInput,
    versionIfExists,
    allowSmartCategorization,
    selectedWorkflowId,
    selectedAskSessionId,
    selectedCollectionId,
    activeCollectionFilter,
    newCollectionName,
    newCollectionDescription,
    newSmartCollectionName,
    newSmartCollectionDescription,
    showMoveToCollectionModal,
    selectedDocumentForMove,
    targetCollectionId,
    collectionSuggestions,
    isLoadingSuggestions,
    showDocumentSuggestionsModal,
    selectedDocumentForSuggestions,
    documentSuggestions,
    isLoadingDocumentSuggestions,
    showPdfViewerModal,
    selectedDocument,
    pdfStreamUrl,
    isLoadingPdf,
    showLinkFileToAskModal,
    selectedDocumentForLink,
    selectedAskSessionIdForLink,
    // Computed values
    documents,
    workflows,
    allCollections,
    regularCollections,
    smartCollections,
    documentsWithCollection,
    filteredDocuments,
    // Helper functions
    getFileCollectionId,
    getFileCollectionInfo,
    // Actions
    getWorkspaceData,
    confirmFileUpload,
    handleUrlUpload,
    handleDelete,
    handleDownload,
    handleViewDocument,
    handleFileSelect,
    handleGetDocumentSuggestions,
    handleMoveToCollection,
    handleCreateCollection,
    handleCreateSmartCollection,
    handleLinkFileToAsk,
    clearProcessingState,
    // Setters
    setShowUploadModal,
    setUploadMode,
    setShowCreateCollectionModal,
    setShowCreateSmartCollectionModal,
    setPendingFiles,
    setUrlInput,
    setVersionIfExists,
    setAllowSmartCategorization,
    setSelectedWorkflowId,
    setSelectedAskSessionId,
    setSelectedCollectionId,
    setActiveCollectionFilter,
    setNewCollectionName,
    setNewCollectionDescription,
    setNewSmartCollectionName,
    setNewSmartCollectionDescription,
    setShowMoveToCollectionModal,
    setSelectedDocumentForMove,
    setTargetCollectionId,
    setCollectionSuggestions,
    setIsLoadingSuggestions,
    setShowDocumentSuggestionsModal,
    setSelectedDocumentForSuggestions,
    setDocumentSuggestions,
    setIsLoadingDocumentSuggestions,
    setShowPdfViewerModal,
    setSelectedDocument,
    setPdfStreamUrl,
    setIsLoadingPdf,
    setIsUploading,
    setUploadError,
    viewMode,
    setViewMode,
    setShowLinkFileToAskModal,
    setSelectedDocumentForLink,
    setSelectedAskSessionIdForLink,
  };
};
